// =============================================================================
// Admin Audit Log Page
// =============================================================================

import { useEffect, useState } from 'react';
import {
  ShieldCheckIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  UserCircleIcon,
  DocumentTextIcon,
  BanknotesIcon,
  KeyIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Card, Button } from '../../components/ui';
import { adminService, AuditLogEntry } from '../../services/admin.service';
import { getApiError } from '../../services/api';
import { formatDateTime } from '../../utils/dateFormat';
import { usePermissions } from '../../hooks/usePermissions';

// =============================================================================
// Component
// =============================================================================

export function AuditLogPage() {
  const { hasPermission, isLoaded: permsLoaded } = usePermissions();

  // Permission guard: block TEAM users without auditLog.view
  if (permsLoaded && !hasPermission('auditLog', 'view')) {
    return (
      <div className="space-y-6">
        <Card padding="lg">
          <div className="text-center py-8">
            <ShieldCheckIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-900">Access Denied</h3>
            <p className="mt-1 text-sm text-gray-500">You do not have permission to view the audit log.</p>
          </div>
        </Card>
      </div>
    );
  }
  const [isLoading, setIsLoading] = useState(true);
  const [events, setEvents] = useState<AuditLogEntry[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [dateRange, setDateRange] = useState<string>('7d');

  useEffect(() => {
    loadAuditEvents();
  }, [dateRange]);

  useEffect(() => {
    filterEvents();
  }, [events, searchQuery, selectedType]);

  const loadAuditEvents = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Calculate date range
      const now = new Date();
      let startDate: string | undefined;
      if (dateRange === '24h') {
        startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
      } else if (dateRange === '7d') {
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      } else if (dateRange === '30d') {
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
      } else if (dateRange === '90d') {
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString();
      }

      const result = await adminService.getAuditLog({
        startDate,
        limit: 100,
      });

      setEvents(result.events);
      setTotal(result.total);
    } catch (err) {
      console.error('Failed to load audit events:', err);
      setError(getApiError(err).message);
    } finally {
      setIsLoading(false);
    }
  };

  const filterEvents = () => {
    let filtered = [...events];

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (event) =>
          event.type.toLowerCase().includes(query) ||
          event.userName?.toLowerCase().includes(query) ||
          event.userEmail?.toLowerCase().includes(query) ||
          event.ipAddress?.includes(query)
      );
    }

    // Filter by type
    if (selectedType !== 'all') {
      filtered = filtered.filter((event) => {
        const eventType = event.type.toUpperCase();
        if (selectedType === 'AUTH') {
          return eventType.includes('LOGIN') || eventType.includes('CODE') || eventType.includes('PASSWORD') || eventType.includes('TOTP');
        }
        if (selectedType === 'DATA_ACCESS') {
          return eventType.includes('VIEWED') || eventType.includes('DOWNLOADED') || eventType.includes('DOCUMENT');
        }
        if (selectedType === 'DATA_MODIFY') {
          return eventType.includes('CREATED') || eventType.includes('UPDATED') || eventType.includes('DELETED') || eventType.includes('EDITED');
        }
        if (selectedType === 'ADMIN') {
          return eventType.includes('INVESTOR') || eventType.includes('DEAL') || eventType.includes('DISTRIBUTION') || eventType.includes('TEMP_ACCESS');
        }
        return true;
      });
    }

    // Sort by timestamp (newest first)
    filtered.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    setFilteredEvents(filtered);
  };

  // Use centralized PST date formatting
  const formatTimestamp = formatDateTime;

  const getActionLabel = (action: string) => {
    const labels: Record<string, string> = {
      LOGIN_SUCCESS: 'Successful Login',
      LOGIN_FAILED: 'Failed Login',
      LOGOUT: 'Logout',
      EMAIL_CODE_REQUESTED: 'Email Code Requested',
      EMAIL_CODE_VERIFIED: 'Email Code Verified',
      SMS_CODE_REQUESTED: 'SMS Code Requested',
      SMS_CODE_VERIFIED: 'SMS Code Verified',
      SMS_CODE_FAILED: 'SMS Code Failed',
      TOTP_SETUP_INITIATED: 'Authenticator Setup Started',
      TOTP_SETUP_COMPLETED: 'Authenticator Setup Completed',
      TOTP_VERIFIED: 'Authenticator Verified',
      TOTP_VERIFICATION_FAILED: 'Authenticator Verification Failed',
      PASSWORD_RESET_REQUESTED: 'Password Reset Requested',
      PASSWORD_RESET_COMPLETED: 'Password Reset Completed',
      SENSITIVE_DATA_VIEWED: 'Sensitive Data Viewed',
      SENSITIVE_DATA_EDITED: 'Sensitive Data Edited',
      DOCUMENT_VIEWED: 'Document Viewed',
      DOCUMENT_DOWNLOADED: 'Document Downloaded',
      DOCUMENT_UPLOADED: 'Document Uploaded',
      DOCUMENT_DELETED: 'Document Deleted',
      PROFILE_CREATED: 'Profile Created',
      PROFILE_UPDATED: 'Profile Updated',
      INVESTOR_CREATED: 'Investor Created',
      INVESTOR_UPDATED: 'Investor Updated',
      INVESTOR_DELETED: 'Investor Deleted',
      DEAL_CREATED: 'Deal Created',
      DEAL_UPDATED: 'Deal Updated',
      DISTRIBUTION_RECORDED: 'Distribution Recorded',
      TEMP_ACCESS_GRANTED: 'Temp Access Granted',
      TEMP_ACCESS_REVOKED: 'Temp Access Revoked',
      ANNOUNCEMENT_SENT: 'Announcement Sent',
    };
    return labels[action] || action.replace(/_/g, ' ');
  };

  const getActionIcon = (type: string) => {
    const upperType = type.toUpperCase();
    if (upperType.includes('LOGIN') || upperType.includes('PASSWORD') || upperType.includes('CODE') || upperType.includes('TOTP')) {
      return <KeyIcon className="h-4 w-4" />;
    }
    if (upperType.includes('DOCUMENT')) {
      return <DocumentTextIcon className="h-4 w-4" />;
    }
    if (upperType.includes('DISTRIBUTION') || upperType.includes('BANKING')) {
      return <BanknotesIcon className="h-4 w-4" />;
    }
    if (upperType.includes('INVESTOR') || upperType.includes('PROFILE') || upperType.includes('DEAL')) {
      return <UserCircleIcon className="h-4 w-4" />;
    }
    if (upperType.includes('FAILED')) {
      return <ExclamationTriangleIcon className="h-4 w-4" />;
    }
    return <ShieldCheckIcon className="h-4 w-4" />;
  };

  const getActionColor = (type: string) => {
    const upperType = type.toUpperCase();
    if (upperType.includes('FAILED')) return 'bg-red-100 text-red-700';
    if (upperType.includes('SENSITIVE') || upperType.includes('BANKING')) return 'bg-yellow-100 text-yellow-700';
    if (upperType.includes('CREATED') || upperType.includes('SUCCESS') || upperType.includes('COMPLETED')) return 'bg-green-100 text-green-700';
    if (upperType.includes('DELETED') || upperType.includes('REVOKED')) return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-700';
  };

  const eventTypes = [
    { value: 'all', label: 'All Events' },
    { value: 'AUTH', label: 'Authentication' },
    { value: 'DATA_ACCESS', label: 'Data Access' },
    { value: 'DATA_MODIFY', label: 'Data Modification' },
    { value: 'ADMIN', label: 'Admin Actions' },
  ];

  const dateRanges = [
    { value: '24h', label: 'Last 24 Hours' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' },
  ];

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-12 bg-gray-200 rounded-lg" />
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-16 bg-gray-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
          <p className="mt-1 text-sm text-gray-500">
            Security and compliance event history (SOC 2)
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => loadAuditEvents()}
          className="flex items-center gap-2"
        >
          <ArrowPathIcon className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <Card padding="md">
          <div className="flex items-center gap-3 text-red-700">
            <ExclamationTriangleIcon className="h-5 w-5" />
            <span className="text-sm">Error: {error}</span>
            <button
              onClick={loadAuditEvents}
              className="text-sm font-medium underline hover:no-underline"
            >
              Retry
            </button>
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card padding="md">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by user, action, IP address..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div className="w-full md:w-48">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {eventTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          <div className="w-full md:w-40">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {dateRanges.map((range) => (
                <option key={range.value} value={range.value}>
                  {range.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Showing {filteredEvents.length} of {total} events
        </p>
      </div>

      {/* Events List */}
      <div className="space-y-2">
        {filteredEvents.map((event) => (
          <Card key={event.id} padding="sm">
            <div className="flex items-start gap-4">
              <div className={`p-2 rounded-lg ${getActionColor(event.type)}`}>
                {getActionIcon(event.type)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-gray-900">
                    {getActionLabel(event.type)}
                  </span>
                  <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                    {event.type.split('_')[0]}
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-gray-500">
                  {event.userName && (
                    <span>
                      User: <span className="text-gray-700">{event.userName}</span>
                    </span>
                  )}
                  {event.userEmail && !event.userName && (
                    <span>
                      Email: <span className="text-gray-700">{event.userEmail}</span>
                    </span>
                  )}
                  {event.ipAddress && (
                    <span>
                      IP: <span className="font-mono text-gray-700">{event.ipAddress}</span>
                    </span>
                  )}
                </div>
                {event.details && Object.keys(event.details).length > 0 && (
                  <p className="mt-1 text-xs text-gray-400">
                    Details: {JSON.stringify(event.details)}
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500 whitespace-nowrap">
                  {formatTimestamp(event.timestamp)}
                </p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {filteredEvents.length === 0 && !error && (
        <Card padding="lg">
          <div className="text-center py-12">
            <ShieldCheckIcon className="h-12 w-12 text-gray-400 mx-auto" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No Events Found</h3>
            <p className="mt-2 text-sm text-gray-500">
              {searchQuery || selectedType !== 'all'
                ? 'Try adjusting your filters'
                : 'No audit events in this time range'}
            </p>
          </div>
        </Card>
      )}

      {/* SOC 2 Notice */}
      <Card padding="sm" className="bg-blue-50 border border-blue-200">
        <div className="flex items-start gap-3">
          <ShieldCheckIcon className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-900">SOC 2 Compliance</p>
            <p className="text-xs text-blue-700 mt-1">
              Audit logs are retained for 7 years per SOC 2 Type II requirements. All security
              events are immutable and timestamped for compliance auditing.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default AuditLogPage;
